import { useMemo } from "react";
import { useEnsText } from "wagmi";
import { mainnet } from "wagmi/chains";
import useVerifiedEnsName from "./useVerifiedEnsName";
import { sanitizeEnsProfile } from "../utils/ensProfilePrefill";

const STALE_TIME = 1000 * 60 * 10;

function useVerifiedEnsText(ensName, key) {
  return useEnsText({
    name: ensName || undefined,
    key,
    chainId: mainnet.id,
    query: {
      enabled: Boolean(ensName),
      staleTime: STALE_TIME,
      retry: false,
    },
  });
}

export default function useVerifiedEnsProfile(address) {
  const verifiedName = useVerifiedEnsName(address);
  const ensName = verifiedName.isEnsVerified ? verifiedName.ensName : null;

  const description = useVerifiedEnsText(ensName, "description");
  const url = useVerifiedEnsText(ensName, "url");
  const twitter = useVerifiedEnsText(ensName, "com.twitter");
  const x = useVerifiedEnsText(ensName, "com.x");
  const github = useVerifiedEnsText(ensName, "com.github");

  const profile = useMemo(
    () =>
      sanitizeEnsProfile({
        avatar: ensName ? verifiedName.avatarUrl : null,
        description: ensName ? description.data : null,
        url: ensName ? url.data : null,
        twitter: ensName ? twitter.data : null,
        x: ensName ? x.data : null,
        github: ensName ? github.data : null,
      }),
    [
      ensName,
      verifiedName.avatarUrl,
      description.data,
      url.data,
      twitter.data,
      x.data,
      github.data,
    ]
  );

  const textRecords = [description, url, twitter, x, github];

  return {
    ...verifiedName,
    profile,
    hasProfileData: Object.values(profile).some(Boolean),
    isProfileLoading: Boolean(
      verifiedName.isLoading ||
        (ensName && textRecords.some((record) => record.isLoading))
    ),
    isProfileError: Boolean(
      verifiedName.isError ||
        textRecords.some((record) => record.isError)
    ),
  };
}
